import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Compass, LifeBuoy, SearchX } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

export default function NotFoundPage({ inApp = false }) {
  const location = useLocation();

  return (
    <div className={inApp ? "min-h-[calc(100vh-7rem)]" : "min-h-screen"}>
      <div className="relative flex min-h-full items-center justify-center overflow-hidden px-4 py-12 sm:px-6 lg:px-10">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(0,0,0,0.05),_transparent_45%),radial-gradient(circle_at_bottom_right,_rgba(0,0,0,0.06),_transparent_36%)]" />

        <Card className="relative w-full max-w-3xl overflow-hidden border-border/90 bg-white/95 shadow-[0_30px_100px_rgba(0,0,0,0.08)]">
          <CardContent className="p-0">
            <div className="grid gap-0 md:grid-cols-[1.1fr_0.9fr]">
              <div className="border-b border-border/70 p-7 md:border-b-0 md:border-r md:p-10">
                <BrandLogo className="mb-8" iconClassName="h-11 w-11 rounded-2xl" textClassName="font-heading text-2xl font-black tracking-tight text-foreground" />

                <div className="inline-flex items-center gap-2 rounded-full border border-border/80 bg-secondary/50 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.24em] text-muted-foreground">
                  <SearchX className="h-3.5 w-3.5" />
                  Page not found
                </div>

                <h1 className="mt-6 font-heading text-4xl font-black tracking-tight text-foreground sm:text-5xl">
                  That page does not exist in UteFlow.
                </h1>
                <p className="mt-4 max-w-xl text-sm leading-7 text-muted-foreground sm:text-base">
                  We couldn&apos;t find <span className="font-semibold text-foreground">{location.pathname}</span>. It may have moved, the link may be out of date, or the address may be wrong.
                </p>

                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                  <Button asChild size="lg" className="h-11 rounded-xl px-6 font-semibold">
                    <Link to={inApp ? "/dashboard" : "/"}>
                      {inApp ? "Back to dashboard" : "Go to home"}
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" size="lg" className="h-11 rounded-xl px-6 font-semibold">
                    <Link to={inApp ? "/rentals" : "/login"}>
                      {inApp ? "Open rentals" : "Go to login"}
                    </Link>
                  </Button>
                </div>
              </div>

              <div className="bg-secondary/35 p-7 md:p-10">
                <div className="rounded-[28px] border border-border/80 bg-background p-5 shadow-[0_16px_50px_rgba(0,0,0,0.05)]">
                  <div className="flex items-center gap-3 border-b border-border/70 pb-4">
                    <div className="grid h-11 w-11 place-items-center rounded-2xl bg-primary text-primary-foreground shadow-sm">
                      <Compass className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="text-sm font-bold uppercase tracking-[0.18em] text-muted-foreground">Quick recovery</div>
                      <div className="mt-1 font-heading text-xl font-black tracking-tight text-foreground">Get back to the right place</div>
                    </div>
                  </div>

                  <div className="mt-5 space-y-3 text-sm leading-6 text-muted-foreground">
                    <div className="rounded-2xl border border-border/70 bg-white/80 p-4">
                      Check the link in your email or browser history if you expected a rental, agreement or billing screen.
                    </div>
                    <div className="rounded-2xl border border-border/70 bg-white/80 p-4">
                      If you are signed in, start from the dashboard or search for the rental, customer or vehicle again.
                    </div>
                    <div className="rounded-2xl border border-border/70 bg-white/80 p-4">
                      If something looks broken, contact support so we can help trace the missing page.
                    </div>
                  </div>

                  <div className="mt-6 flex items-center gap-3 rounded-2xl border border-border/70 bg-white/80 p-4">
                    <div className="grid h-10 w-10 place-items-center rounded-2xl bg-primary text-primary-foreground">
                      <LifeBuoy className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">Need help?</div>
                      <div className="mt-1 text-sm font-semibold text-foreground">support@uteflow.com.au</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
